import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth';
import type { Campaign } from '@marnic/shared';

interface CampaignRow extends Campaign {
  leadCount: number;
}

export function Campaigns(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  const campaigns = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<CampaignRow[]>('/campaigns'),
  });

  const [creating, setCreating] = useState(false);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Campaigns</h1>
        {user && user.role !== 'agent' && (
          <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
            + New campaign
          </button>
        )}
      </header>

      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {campaigns.data?.map((c) => (
          <Link to={`/campaigns/${c.id}`} key={c.id} className="card p-4 hover:shadow-md transition">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold">{c.name}</h2>
              <span
                className={`badge ${
                  c.status === 'active'
                    ? 'bg-emerald-100 text-emerald-700'
                    : c.status === 'paused'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-ink-100 text-ink-700'
                }`}
              >
                {c.status}
              </span>
            </div>
            <p className="text-xs text-ink-500 mt-1 capitalize">
              {c.diallerMode} · {c.linesPerAgent} line{c.linesPerAgent === 1 ? '' : 's'} / agent
            </p>
            {c.description && (
              <p className="mt-2 text-sm text-ink-600 line-clamp-2">{c.description}</p>
            )}
            <div className="mt-3 flex items-center gap-3 text-xs text-ink-500">
              <span>{c.leadCount} leads</span>
              <span>·</span>
              <span>Threshold {(c.abandonedCallThreshold * 100).toFixed(1)}%</span>
            </div>
          </Link>
        ))}
        {campaigns.data?.length === 0 && (
          <p className="text-sm text-ink-500">No campaigns yet.</p>
        )}
      </div>

      {creating && (
        <CreateCampaignModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            void qc.invalidateQueries({ queryKey: ['campaigns'] });
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function CreateCampaignModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [diallerMode, setDiallerMode] = useState<'preview' | 'progressive' | 'power'>('preview');
  const [linesPerAgent, setLinesPerAgent] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post<Campaign>('/campaigns', { name, description, diallerMode, linesPerAgent }),
    onSuccess: onCreated,
    onError: (err: unknown) => {
      setError((err as { code?: string }).code ?? 'Could not create');
    },
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink-900/40 p-4">
      <div className="card w-full max-w-lg p-4">
        <h3 className="text-lg font-semibold">New campaign</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className="label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Mode</label>
              <select className="input" value={diallerMode} onChange={(e) => setDiallerMode(e.target.value as typeof diallerMode)}>
                <option value="preview">Preview</option>
                <option value="progressive">Progressive</option>
                <option value="power">Power</option>
              </select>
            </div>
            <div>
              <label className="label">Lines per agent</label>
              <input type="number" className="input" min={1} max={3} value={linesPerAgent} onChange={(e) => setLinesPerAgent(parseInt(e.target.value, 10) || 1)} />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => create.mutate()}
            disabled={create.isPending || !name.trim()}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}
